/*
Copyright 2026 AB Civil Consulting

Electron Overlay Bridge - Optimized version
*/

import { logger as rootLogger } from "matrix-js-sdk/lib/logger";

const logger = rootLogger.getChild("[ElectronOverlay]");

const MESSAGE_TYPE = "element-call-annotation";

let cachedVideoWidth = 0;
let cachedVideoHeight = 0;
let detectionAttempts = 0;
let lastUpdateJson = "";

const hasDirectElectronAccess = (): boolean => {
  return typeof window !== "undefined" &&
         "electron" in window &&
         typeof (window as any).electron?.annotationOverlay !== "undefined";
};

const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

const detectDimensions = (): { width: number; height: number } | null => {
  detectionAttempts++;

  const videos = document.querySelectorAll("video");
  for (const video of videos) {
    if (video.videoWidth > 0 && video.videoHeight > 0) {
      return { width: video.videoWidth, height: video.videoHeight };
    }
  }

  for (const video of videos) {
    const stream = (video as any).srcObject as MediaStream | null;
    if (stream) {
      const tracks = stream.getVideoTracks();
      for (const track of tracks) {
        const settings = track.getSettings();
        if (settings.width && settings.height) {
          return { width: settings.width, height: settings.height };
        }
      }
    }
  }

  if (detectionAttempts > 10 && cachedVideoWidth === 0) {
    const dpr = window.devicePixelRatio || 1;
    const screenW = Math.round(window.screen.width * dpr);
    const screenH = Math.round(window.screen.height * dpr);
    console.log("[ElectronOverlay] Using screen fallback:", screenW, "x", screenH);
    return { width: screenW, height: screenH };
  }

  return null;
};

const sendToParent = (action: string, data?: any): void => {
  if (!isInIframe()) return;
  try {
    window.parent.postMessage({ type: MESSAGE_TYPE, action, data }, "*");
  } catch (e) {
    // Silent
  }
};

const getOverlayApi = (): any | null => {
  if (hasDirectElectronAccess()) {
    return (window as any).electron.annotationOverlay;
  }
  return null;
};

export const showOverlay = (): void => {
  const api = getOverlayApi();
  if (api) api.show();
  else if (isInIframe()) sendToParent("show");
};

export const hideOverlay = (): void => {
  const api = getOverlayApi();
  if (api) api.hide();
  else if (isInIframe()) sendToParent("hide");
};

export const destroyOverlay = (): void => {
  const api = getOverlayApi();
  if (api) api.destroy();
  else if (isInIframe()) sendToParent("destroy");
};

export const updateOverlayAnnotations = (annotations: any[], pendingStrokes: any[]): void => {
  const hasContent = annotations.length > 0 || pendingStrokes.length > 0;

  // Skip if no content and no dimensions have been established yet
  if (!hasContent && cachedVideoWidth === 0) {
    return;
  }

  // Dedupe identical updates (but only if dimensions are available)
  const json = JSON.stringify({ annotations, pendingStrokes });
  if (json === lastUpdateJson && hasContent && cachedVideoWidth > 0) {
    return;
  }

  // Only update lastUpdateJson if there's actual content
  if (hasContent) {
    lastUpdateJson = json;
  }

  // Ensure we have dimensions - use screen as fallback
  if (cachedVideoWidth === 0) {
    const dpr = window.devicePixelRatio || 1;
    cachedVideoWidth = Math.round(window.screen.width * dpr);
    cachedVideoHeight = Math.round(window.screen.height * dpr);
    console.log("[ElectronOverlay] Using screen fallback for update:", cachedVideoWidth, "x", cachedVideoHeight);
  }

  const payload = {
    annotations,
    pendingStrokes,
    captureWidth: cachedVideoWidth,
    captureHeight: cachedVideoHeight
  };

  const api = getOverlayApi();
  if (api) {
    api.update(payload);
  } else if (isInIframe()) {
    sendToParent("update", payload);
  }
};

export const setCaptureFrameInfo = (width: number, height: number): void => {
  console.log("[ElectronOverlay] setCaptureFrameInfo:", width, "x", height);
  cachedVideoWidth = width;
  cachedVideoHeight = height;
  const api = getOverlayApi();
  if (api) api.setCaptureFrameInfo({ width, height });
  else if (isInIframe()) sendToParent("setCaptureFrameInfo", { width, height });
};

export const notifyScreenShareStarted = (info?: any): void => {
  console.log("[ElectronOverlay] screenShareStarted:", info);
  detectionAttempts = 0;
  if (info?.frameWidth && info?.frameHeight) {
    cachedVideoWidth = info.frameWidth;
    cachedVideoHeight = info.frameHeight;
  } else {
    // Immediately try to detect dimensions or use screen fallback
    const dims = detectDimensions();
    if (dims) {
      cachedVideoWidth = dims.width;
      cachedVideoHeight = dims.height;
      console.log("[ElectronOverlay] Immediate detection:", dims.width, "x", dims.height);
    } else {
      // Use screen resolution as immediate fallback for screen shares
      const dpr = window.devicePixelRatio || 1;
      cachedVideoWidth = Math.round(window.screen.width * dpr);
      cachedVideoHeight = Math.round(window.screen.height * dpr);
      console.log("[ElectronOverlay] Screen fallback on start:", cachedVideoWidth, "x", cachedVideoHeight);
    }
  }
  const api = getOverlayApi();
  if (api) api.screenShareStarted(info);
  else if (isInIframe()) sendToParent("screenShareStarted", info);

  // Send dimensions immediately
  if (cachedVideoWidth > 0 && cachedVideoHeight > 0) {
    if (api) api.setCaptureFrameInfo({ width: cachedVideoWidth, height: cachedVideoHeight });
    else if (isInIframe()) sendToParent("setCaptureFrameInfo", { width: cachedVideoWidth, height: cachedVideoHeight });
  }
};

export const notifyScreenShareStopped = (): void => {
  cachedVideoWidth = 0;
  cachedVideoHeight = 0;
  lastUpdateJson = "";
  const api = getOverlayApi();
  if (api) api.screenShareStopped();
  else if (isInIframe()) sendToParent("screenShareStopped");
};

export const isOverlayAvailable = (): boolean => hasDirectElectronAccess() || isInIframe();
export const isElectron = hasDirectElectronAccess;

if (typeof window !== "undefined") {
  setInterval(() => {
    const dims = detectDimensions();
    if (dims && (dims.width !== cachedVideoWidth || dims.height !== cachedVideoHeight)) {
      cachedVideoWidth = dims.width;
      cachedVideoHeight = dims.height;
      console.log("[ElectronOverlay] Detected:", dims.width, "x", dims.height);
      if (isInIframe()) sendToParent("setCaptureFrameInfo", { width: dims.width, height: dims.height });
    }
  }, 2000);
}

console.log("[ElectronOverlay] Module loaded");
